import { useState, useEffect, useRef, Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Gavel,
  LayoutDashboard,
  Building2,
  ClipboardList,
  ShieldCheck,
  AlertTriangle,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  User,
  Users,
  Bell,
  PhoneCall,
  Plug, FolderLock,
  Webhook,
  CreditCard,
  ScrollText,
  FileText,
  UserCheck,
  Library,
  ClipboardCheck,
  ListChecks,
  BarChart3,
  Shield,
  KeyRound,
  BadgeCheck,
  BookOpen,
  LifeBuoy,
  Image as ImageIcon,
  CalendarClock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/store/ui.store';
import { useAuthStore } from '@/store/auth.store';
import { useBranding } from '@/api/hooks/useSettings';

// ── Types ─────────────────────────────────────────────────────────────────────

type GroupId = 'notifications' | 'tprm' | 'compliance' | 'intelligence' | 'settings';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  roles?: string[];
  module?: string;
}

interface NavGroup {
  id: GroupId;
  label: string;
  icon: React.ElementType;
  defaultExpanded: boolean;
  items: NavItem[];
}

// ── localStorage persistence ──────────────────────────────────────────────────

const STORAGE_KEY = 'veriaxis.nav.groupState.v1';

const DEFAULT_GROUP_STATE: Record<GroupId, boolean> = {
  notifications: true,
  tprm: true,
  compliance: true,
  intelligence: true,
  settings: false,
};

function loadGroupState(): Record<GroupId, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_GROUP_STATE };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      notifications:       typeof parsed.notifications       === 'boolean' ? parsed.notifications       : DEFAULT_GROUP_STATE.notifications,
      tprm:                typeof parsed.tprm                === 'boolean' ? parsed.tprm                : DEFAULT_GROUP_STATE.tprm,
      compliance:          typeof parsed.compliance          === 'boolean' ? parsed.compliance          : DEFAULT_GROUP_STATE.compliance,
      intelligence:        typeof parsed.intelligence        === 'boolean' ? parsed.intelligence        : DEFAULT_GROUP_STATE.intelligence,
      settings:            typeof parsed.settings            === 'boolean' ? parsed.settings            : DEFAULT_GROUP_STATE.settings,
    };
  } catch {
    return { ...DEFAULT_GROUP_STATE };
  }
}

function saveGroupState(state: Record<GroupId, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable — silent
  }
}

// ── Nav config ────────────────────────────────────────────────────────────────

const PINNED_HOME: NavItem = {
  label: 'Home',
  href: '/dashboard',
  icon: LayoutDashboard,
};

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    defaultExpanded: true,
    items: [
      { label: 'Notifications', href: '/notifications', icon: Bell },
    ],
  },
  {
    id: 'tprm',
    label: 'Vendor Risk',
    icon: Shield,
    defaultExpanded: true,
    items: [
      { label: 'Vendors',                   href: '/vendors',          icon: Building2,    roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer'],                    module: 'vrm' },
      { label: 'Assessments',               href: '/assessments',      icon: ClipboardList, roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer'],                   module: 'vrm' },
      // ⚠ RET-10: the Vendor Risk Register (was "Vendor Risk Responses") is back under Vendor Risk — it is part of VRM,
      //   gated by VRM in the menu, on the route and at the api (D-RR-SURFACES-MODULE-GATED). Route unchanged.
      { label: 'Vendor Risk Register',      href: '/risk-acceptances', icon: AlertTriangle, roles: ['super_admin', 'risk_manager'],                                              module: 'vrm' },
      { label: 'Assessment Templates',      href: '/templates',        icon: FileText,     roles: ['super_admin', 'risk_manager', 'analyst', 'compliance_manager'],                  module: 'vrm' },
      { label: 'Assessment Library',        href: '/templates/hub',    icon: Library,      roles: ['super_admin', 'risk_manager', 'compliance_manager'],                             module: 'vrm' },
    ],
  },
  // ⚠ RET-10: the Risk Management group is RETIRED — the Enterprise Risk Dashboard is removed (D-RET10-1), the Vendor
  //   Risk Register moved to Vendor Risk and the Risk Register to Compliance Program.
  {
    id: 'compliance',
    label: 'Compliance Program',
    icon: ShieldCheck,
    defaultExpanded: true,
    items: [
      // ⚠ Compliance Dashboard: relocated from the (now-removed) Dashboards group; route /dashboards/compliance UNCHANGED.
      // ⚠ RET-10: Compliance-gated (it was the group's one untagged item — shown to tenants without Compliance).
      { label: 'Compliance Dashboard', href: '/dashboards/compliance',      icon: BarChart3,     roles: ['super_admin', 'risk_manager', 'analyst', 'executive_viewer', 'compliance_manager'], module: 'compliance' },
      // ⚠ TC3 — the rebuilt Frameworks workspace (cts_ + ccl_, derived progress). The legacy
      // 'Frameworks' entry below still points at /compliance (pol_-based, stored percentages) and
      // runs in PARALLEL until TC6; the two are deliberately named differently so nobody has to
      // guess which is which while both exist.
      { label: 'Framework Programs',   href: '/compliance/workspace/frameworks', icon: ShieldCheck, roles: ['super_admin', 'risk_manager', 'analyst', 'compliance_manager'], module: 'compliance' },
      // ⚠ TC2 — THE REACHABILITY FIX. /compliance/controls has been ROUTED since CC Inc1 S4 and
      // has never appeared in this nav: the only ways in were a dashboard scorecard click and an
      // assignment deep link, both of which land on the LEGACY /compliance/controls/:frameworkId
      // rather than the workspace. Routed is not reachable. The rebuilt workspace is nav-placed
      // here from its first render.
      { label: 'Controls',             href: '/compliance/workspace/controls', icon: ListChecks, roles: ['super_admin', 'risk_manager', 'analyst', 'compliance_manager'], module: 'compliance' },
      // ⚠ Directly after Controls, so the group reads my-controls → library-to-add-from →
      //    frameworks. Same roles: browsing the library is a working act, and gating it more
      //    tightly than the workspace it feeds would strand exactly the people who use it.
      { label: 'Control Library',       href: '/compliance/control-library', icon: Library, roles: ['super_admin', 'risk_manager', 'analyst', 'compliance_manager'], module: 'compliance' },
      // ⚠ TC4 — the Tasks workspace and the program surfaces. The legacy 'My Tasks' and 'Team
      // Queue' entries below point at the pol_ assignment machine and run in PARALLEL until TC6;
      // they are named differently on purpose so nobody has to guess which is which while both
      // exist.
      { label: 'Tasks',                href: '/compliance/workspace/tasks',    icon: ClipboardCheck, module: 'compliance' },
      // ⚠ TC5 — the tenant-facing Audit Center. Reachable: routed is not reachable, and a page
      // nobody can navigate to is a page that does not exist. ⓘ Settings > Auditor Access remains
      // where an auditor is INVITED; this is where the tenant watches the audit it produced.
      { label: 'Audit Center',         href: '/compliance/workspace/audit',    icon: Gavel, module: 'compliance' },
      { label: 'Evidence Repository',  href: '/compliance/workspace/tasks?tab=evidence',   icon: FileText,      roles: ['super_admin', 'risk_manager', 'compliance_manager'],           module: 'compliance' },
      { label: 'Framework Exceptions', href: '/compliance/workspace/tasks?tab=exceptions', icon: AlertTriangle, roles: ['super_admin', 'risk_manager', 'compliance_manager'],           module: 'compliance' },
      // ⚠ RET-10: the Risk Register (the organization's risk responses; was "Org Risk Responses") re-homed here and
      //   Compliance-gated in the menu, on the route and at the api (D-RR-SURFACES-MODULE-GATED). Route unchanged.
      { label: 'Risk Register',        href: '/org-risk-acceptances',          icon: AlertTriangle, roles: ['risk_manager', 'super_admin'],                                     module: 'compliance' },
    ],
  },
];

// ── Settings destinations (single source of truth: sidebar + hub + resolvers) ───

export type SettingsCategory =
  | 'Account & Organization'
  | 'Users & Access'
  | 'Security & Assurance'
  | 'Notifications & Escalation'
  | 'Integrations & Automation'
  | 'Support';

export const SETTINGS_CATEGORY_ORDER: SettingsCategory[] = [
  'Account & Organization',
  'Users & Access',
  'Security & Assurance',
  'Notifications & Escalation',
  'Integrations & Automation',
  'Support',
];

export interface SettingsDestination {
  label: string;
  /** Route. Omitted for non-navigable tiles (e.g. coming-soon). */
  href?: string;
  icon: React.ElementType;
  category: SettingsCategory;
  /** Rendered in the slim 4-item sidebar (always also appears in the hub). */
  slim?: boolean;
  /** Hub-tile visibility. 'all' = every authenticated user; else any-of-roles. */
  tileRoles: string[] | 'all';
  /** One-line description for the hub tile. */
  description?: string;
  /** Non-navigable, visually muted "coming soon" tile (no route). */
  comingSoon?: boolean;
  /**
   * ⚠ CAPR S3 — a module-scoped setting is offered only to a tenant entitled to that module
   * (D-MODULE-INDEPENDENCE): the tile must not offer a page whose api the module gate refuses.
   */
  module?: string;
}

const RM_SA = ['risk_manager', 'super_admin'];
// ⚠ super_admin ONLY — connecting the repository that holds the organisation's evidence, and
//   running an admin script to do it, is a higher-consequence act than the RM_SA settings beside
//   it. The tile must not offer a page the API will refuse (amendment B).
const SA_ONLY = ['super_admin'];

// SMTP excluded. Support Verification's tile is 'all' by design (D-HUB-1A-6: the page
// is reachable down to Member). Account Hygiene is surfaced as "Security Posture"
// (label only — route /settings/account-hygiene + API unchanged, D-HUB-2-2). HUB-2
// adds the Support category (SV moved here from Security & Assurance, not duplicated).
export const SETTINGS_DESTINATIONS: SettingsDestination[] = [
  { label: 'Profile',              href: '/settings/profile',              icon: User,        category: 'Account & Organization',    slim: true, tileRoles: 'all',           description: 'Manage your personal details, preferences, and sign-in settings.' },
  { label: 'Billing & Plans',      href: '/settings/billing',              icon: CreditCard,  category: 'Account & Organization',                 tileRoles: ['super_admin'], description: 'Review your subscription, payment methods, and plan usage.' },
  { label: 'Custom Branding',      href: '/settings/branding',             icon: ImageIcon,   category: 'Account & Organization',                 tileRoles: ['super_admin'], description: "Bring your organization's logo and colors to the workspace." },
  // USER_ADMIN cluster: every tenant user reads the list (read-only on user management); managing it is gated server-side.
  { label: 'Users & Permissions',  href: '/settings/team',                 icon: Users,       category: 'Users & Access',            slim: true, tileRoles: 'all',           description: 'Invite members, assign roles, and manage access across your organization.' },
  { label: 'Single Sign-On',       href: '/settings/sso',                  icon: KeyRound,    category: 'Users & Access',                        tileRoles: RM_SA,           description: 'Connect your identity provider for secure, centralized sign-in.' },
  { label: 'Auditor Access',       href: '/settings/auditor-access',       icon: UserCheck,   category: 'Users & Access',                        tileRoles: RM_SA,           description: 'Grant time-boxed, read-only access to external auditors.' },
  { label: 'Assessment Review',    href: '/settings/assessment-review',    icon: CalendarClock, category: 'Security & Assurance',                 tileRoles: RM_SA, module: 'compliance', description: 'Set how often controls are re-assessed by default.' },
  { label: 'Security Posture',     href: '/settings/account-hygiene',      icon: ShieldCheck, category: 'Security & Assurance',                   tileRoles: RM_SA,           description: 'Track account security health and resolve configuration risks.' },
  { label: 'Activity Log',         href: '/settings/audit-log',            icon: ScrollText,  category: 'Security & Assurance',       slim: true, tileRoles: RM_SA,           description: 'Review a complete audit trail of activity across your tenant.' },
  { label: 'Notifications',        href: '/settings/notifications',        icon: Bell,        category: 'Notifications & Escalation',            tileRoles: RM_SA,           description: 'Control which events trigger alerts and how your team is notified.' },
  { label: 'Escalation Contacts',  href: '/settings/escalation-contacts',  icon: PhoneCall,   category: 'Notifications & Escalation',            tileRoles: RM_SA,           description: 'Define who is alerted, and in what order, when issues arise.' },
  { label: 'Evidence Repositories', href: '/settings/evidence-repositories', icon: FolderLock, category: 'Integrations & Automation',           tileRoles: SA_ONLY,         description: 'Designate where VeriGRC keeps evidence for your controls — one location you own.' },
  { label: 'Integrations',         href: '/settings/integrations',         icon: Plug,        category: 'Integrations & Automation',             tileRoles: RM_SA,           description: 'Connect VeriGRC to the tools your team relies on.' },
  { label: 'Webhooks',             href: '/settings/webhooks',             icon: Webhook,     category: 'Integrations & Automation',             tileRoles: RM_SA,           description: 'Stream real-time events to your own endpoints and workflows.' },
  { label: 'Support Verification', href: '/settings/support-verification', icon: BadgeCheck,  category: 'Support',                                tileRoles: 'all',           description: 'Generate a secure one-time code to verify your identity with Support.' },
  { label: 'Support Contacts',     href: '/settings/support-contacts',     icon: LifeBuoy,    category: 'Support',                                tileRoles: 'all',           description: 'Reach our support team.' },
  { label: 'Help & Documentation',                                         icon: BookOpen,    category: 'Support',                                tileRoles: 'all',           description: 'Guides, references, and best practices for VeriGRC.', comingSoon: true },
];

/** Hub-tile visibility for a destination given the user's role check. */
export function isSettingsTileVisible(
  dest: SettingsDestination,
  hasAnyRole: (roles: string[]) => boolean,
  entitledModules: readonly string[],
): boolean {
  if (dest.module && !entitledModules.includes(dest.module)) return false;
  return dest.tileRoles === 'all' || hasAnyRole(dest.tileRoles);
}

/** Slim 4-item sidebar subset (Profile, Users & Permissions, Activity Log). */
const SLIM_SETTINGS: SettingsDestination[] = SETTINGS_DESTINATIONS.filter(d => d.slim);

/** Synthetic slim-sidebar entry → the All Settings hub (not a real destination). */
const ALL_SETTINGS_HREF = '/settings';

// ── Active-route helpers ──────────────────────────────────────────────────────

const ALL_ITEM_HREFS: string[] = [
  PINNED_HOME.href,
  ...NAV_GROUPS.flatMap(g => g.items.map(i => i.href)),
  ...SETTINGS_DESTINATIONS.map(i => i.href).filter((h): h is string => Boolean(h)),
];

/** Returns the href of the nav item whose prefix best matches the current pathname. */
function resolveActiveHref(pathname: string): string {
  if (pathname === '/' || pathname === '/dashboard') return '/dashboard';
  const matches = ALL_ITEM_HREFS.filter(h => h !== '/dashboard' && pathname.startsWith(h));
  if (matches.length === 0) return '';
  return matches.reduce((a, b) => (a.length >= b.length ? a : b));
}

/** Returns which group (or 'home' / 'settings') contains the given active href. */
function resolveGroup(href: string): GroupId | 'home' | 'settings' | null {
  if (href === '/dashboard') return 'home';
  for (const g of NAV_GROUPS) {
    if (g.items.some(i => i.href === href)) return g.id;
  }
  if (SETTINGS_DESTINATIONS.some(i => i.href === href)) return 'settings';
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const { roles, user, entitledModules } = useAuthStore();
  const location = useLocation();
  const { data: branding } = useBranding();

  const [groupState, setGroupState] = useState<Record<GroupId, boolean>>(() => loadGroupState());
  const [settingsFlyoutOpen, setSettingsFlyoutOpen] = useState(false);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);

  const activeHref  = resolveActiveHref(location.pathname);
  const activeGroup = resolveGroup(activeHref);

  // Auto-expand the group containing the active route, persist to localStorage
  useEffect(() => {
    if (!activeGroup || activeGroup === 'home') return;
    setGroupState(prev => {
      if (prev[activeGroup as GroupId]) return prev; // already expanded — no change
      const next = { ...prev, [activeGroup]: true };
      saveGroupState(next);
      return next;
    });
  }, [activeGroup]);

  function canSee(item: NavItem): boolean {
    if (item.module && !entitledModules.includes(item.module)) return false;
    if (!item.roles) return true;
    return item.roles.some(r => roles.includes(r));
  }

  function toggleGroup(id: GroupId) {
    setGroupState(prev => {
      const next = { ...prev, [id]: !prev[id] };
      saveGroupState(next);
      return next;
    });
  }

  const onSettingsPage = location.pathname.startsWith('/settings');

  // Hub-tile visibility uses the canonical any-of-roles check (Profile/Support
  // Verification are 'all'). Decision-C: show "All Settings" only when the hub is
  // non-empty for this user (always true in current state since Profile is 'all').
  const hasAnyRole = (rs: string[]) => rs.some(r => roles.includes(r));
  const hubHasVisibleTile = SETTINGS_DESTINATIONS.some(d => isSettingsTileVisible(d, hasAnyRole, entitledModules));

  return (
    <aside
      className={cn(
        'relative flex h-full flex-col border-r border-border-subtle bg-bg-surface transition-all duration-200',
        sidebarCollapsed ? 'w-[60px]' : 'w-[240px]',
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border-subtle px-4',
          sidebarCollapsed ? 'justify-center' : 'gap-2.5',
        )}
      >
        {branding?.hasLogo && branding.logoUrl ? (
          <>
            <img
              src={branding.logoUrl}
              alt={user?.tenantName ?? 'Logo'}
              style={{
                maxWidth: sidebarCollapsed ? 32 : 140,
                maxHeight: sidebarCollapsed ? 32 : 36,
                objectFit: 'contain',
              }}
              className="shrink-0"
            />
            {!sidebarCollapsed && user?.tenantName && (
              <p className="truncate text-base font-semibold text-text-secondary leading-tight">
                {user.tenantName}
              </p>
            )}
          </>
        ) : (
          <>
            <img
              src="/verigrc-shield-256.png"
              alt="VeriGRC"
              className="h-7 w-7 shrink-0 object-contain"
            />
            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1">
                <span className="text-sm font-bold tracking-tight text-text-primary">VeriGRC</span>
                {user?.tenantName && (
                  <p className="truncate text-base font-semibold text-text-secondary leading-tight">
                    {user.tenantName}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 scrollbar-none">

        {/* Pinned Home */}
        <SidebarItem
          item={PINNED_HOME}
          collapsed={sidebarCollapsed}
          active={activeHref === '/dashboard'}
        />

        {/* Separator below Home */}
        <div className="mx-3 my-2 border-t border-white/15" />

        {/* Collapsible groups */}
        {NAV_GROUPS.map((group, index) => {
          const visibleItems = group.items.filter(canSee);
          if (visibleItems.length === 0) return null;
          const isExpanded = groupState[group.id];
          const separator = index < NAV_GROUPS.length - 1
            ? <div className="mx-3 my-2 border-t border-white/15" />
            : null;

          if (sidebarCollapsed) {
            // Narrow mode: flat icons only, no group headers
            return (
              <Fragment key={group.id}>
                <div>
                  {visibleItems.map(item => (
                    <SidebarItem
                      key={item.href}
                      item={item}
                      collapsed={true}
                      active={activeHref === item.href}
                    />
                  ))}
                </div>
                {separator}
              </Fragment>
            );
          }

          return (
            <Fragment key={group.id}>
              <div className="mb-1">
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="group mx-2 flex w-[calc(100%-16px)] items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors text-text-faint hover:bg-bg-surface-2 hover:text-text-primary"
                >
                  <span className="flex-1 truncate text-left text-[10px] font-semibold uppercase tracking-wider">
                    {group.label}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div>
                    {visibleItems.map(item => (
                      <SidebarItem
                        key={item.href}
                        item={item}
                        collapsed={false}
                        active={activeHref === item.href}
                      />
                    ))}
                  </div>
                )}
              </div>
              {separator}
            </Fragment>
          );
        })}

      </nav>

      {/* Settings — pinned above user card as direct flex child of aside */}
      <div className="mx-3 my-2 border-t border-white/15" />
      {sidebarCollapsed ? (
        <NavLink
          to="/settings/profile"
          title="Settings"
          className={cn(
            'mx-2 flex items-center justify-center rounded-lg px-2 py-1.5 text-sm transition-colors',
            onSettingsPage
              ? 'bg-accent-cyan-dim text-accent-cyan'
              : 'text-text-secondary hover:bg-bg-surface-2 hover:text-text-primary',
          )}
        >
          <Settings className={cn('h-4 w-4 shrink-0', onSettingsPage && 'text-accent-cyan')} />
        </NavLink>
      ) : (
        <>
          <div className="mb-1">
            <button
              type="button"
              onClick={() => toggleGroup('settings')}
              className={cn(
                'group mx-2 flex w-[calc(100%-16px)] items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
                onSettingsPage
                  ? 'text-accent-cyan'
                  : 'text-text-faint hover:bg-bg-surface-2 hover:text-text-primary',
              )}
            >
              <span className="flex-1 truncate text-left text-[10px] font-semibold uppercase tracking-wider">
                Settings
              </span>
              {groupState.settings ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              )}
            </button>

            {/* The Settings group expands inline to its quick sub-items (Profile / Users &
                Permissions / Activity Log). "All Settings" is NOT here — it's standalone below. */}
            {groupState.settings && (
              <div>
                {SLIM_SETTINGS.filter(d => d.href && isSettingsTileVisible(d, hasAnyRole, entitledModules)).map(item => (
                  <SidebarItem
                    key={item.href}
                    item={{ label: item.label, href: item.href!, icon: item.icon }}
                    collapsed={false}
                    active={activeHref === item.href}
                  />
                ))}
              </div>
            )}
          </div>

          {/* "All Settings" — standalone top-level item (F2-revise, MI final): the WHOLE row toggles
              the auto-populated mega-menu; it does NOT navigate to /settings. The flyout's
              "View all settings →" footer is the only path to /settings from here. */}
          {hubHasVisibleTile && (
            <div className="relative mb-1">
              <button
                ref={settingsTriggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={settingsFlyoutOpen}
                onClick={() => setSettingsFlyoutOpen(o => !o)}
                className={cn(
                  'group mx-2 flex w-[calc(100%-16px)] items-center gap-2.5 rounded-lg border-l-2 px-2 py-1.5 text-sm transition-colors',
                  settingsFlyoutOpen
                    ? 'border-accent-cyan bg-bg-surface-2 text-text-primary'
                    : 'border-transparent text-text-secondary hover:bg-bg-surface-2 hover:text-text-primary',
                )}
              >
                <Settings className="h-4 w-4 shrink-0" />
                <span className="truncate font-medium">All Settings</span>
                <ChevronRight className={cn('ml-auto h-3.5 w-3.5 shrink-0 transition-transform', settingsFlyoutOpen && 'rotate-90')} />
              </button>
              {settingsFlyoutOpen && (
                <SettingsFlyout
                  hasAnyRole={hasAnyRole}
                  onClose={() => setSettingsFlyoutOpen(false)}
                  triggerRef={settingsTriggerRef}
                />
              )}
            </div>
          )}
        </>
      )}

      {/* User card — pinned bottom */}
      {!sidebarCollapsed && user && (
        <div className="border-t border-border-subtle py-2">
          <div className="mx-2 flex items-center gap-2.5 rounded-lg px-2 py-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-cyan-dim text-xs font-semibold text-accent-cyan">
              {user.displayName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-text-primary">{user.displayName}</p>
              <p className="truncate text-[10px] text-text-secondary">{user.email}</p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-16 flex h-6 w-6 items-center justify-center rounded-full border border-border-subtle bg-bg-surface text-text-secondary shadow-sm transition-colors hover:bg-bg-surface-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? (
          <ChevronRight className="h-3 w-3" />
        ) : (
          <ChevronLeft className="h-3 w-3" />
        )}
      </button>
    </aside>
  );
}

// ── SidebarItem ───────────────────────────────────────────────────────────────

interface SidebarItemProps {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
}

function SidebarItem({ item, collapsed, active }: SidebarItemProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      title={collapsed ? item.label : undefined}
      className={cn(
        'group mx-2 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition-colors',
        active
          ? 'bg-accent-cyan-dim text-text-primary shadow-[inset_3px_0_0_theme(colors.accent.cyan)]'
          // Inactive nav-destination links use the brighter text-nav (Slice A) for readability.
          // Active stays text-primary + accent bg/bar, so the active/inactive contrast is preserved.
          : 'text-text-nav hover:bg-bg-surface-2 hover:text-text-primary',
        collapsed && 'justify-center',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate font-medium">{item.label}</span>}
    </NavLink>
  );
}

// ── SettingsFlyout ────────────────────────────────────────────────────────────
// Compact Settings mega-menu opened from the "All Settings" sidebar trigger. Contents
// AUTO-POPULATE from SETTINGS_DESTINATIONS / SETTINGS_CATEGORY_ORDER (verbatim reuse — the
// same single source of truth SettingsHubPage renders) and honor the SAME per-tile role
// visibility via isSettingsTileVisible. Layout follows the Claude Design template (2 columns,
// group headings, footer) using EXISTING customer-app tokens only (no new tokens / inline hex).
function SettingsFlyout({
  hasAnyRole,
  onClose,
  triggerRef,
}: {
  hasAnyRole: (roles: string[]) => boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { entitledModules } = useAuthStore();

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, triggerRef]);

  const groups = SETTINGS_CATEGORY_ORDER.map(category => ({
    category,
    tiles: SETTINGS_DESTINATIONS.filter(d => d.category === category && isSettingsTileVisible(d, hasAnyRole, entitledModules)),
  })).filter(g => g.tiles.length > 0);

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label="Settings menu"
      className="absolute bottom-0 left-full z-50 ml-2 w-[580px] overflow-hidden rounded-xl border border-border-subtle bg-bg-surface shadow-2xl"
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-border-subtle bg-bg-surface-2 px-4 py-3">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-text-secondary">Settings menu</span>
        <span className="text-[9.5px] tracking-wide text-text-faint">Esc to close</span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-4 px-4 pb-2 pt-4">
        {groups.map(({ category, tiles }) => (
          <div key={category} className="flex flex-col gap-1">
            <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.13em] text-text-secondary">
              {category}
            </div>
            {tiles.map(tile =>
              tile.comingSoon ? (
                <div
                  key={tile.label}
                  aria-disabled="true"
                  className="flex items-center gap-2 rounded-md border-l-2 border-transparent px-2.5 py-1.5 text-[13.5px] text-text-faint"
                >
                  <span className="truncate">{tile.label}</span>
                  <span className="ml-auto rounded border border-border-subtle px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-text-faint">
                    Soon
                  </span>
                </div>
              ) : (
                <NavLink
                  key={tile.href}
                  to={tile.href!}
                  onClick={onClose}
                  className="rounded-md border-l-2 border-transparent px-2.5 py-1.5 text-[13.5px] text-text-nav outline-none transition-colors hover:border-accent-cyan hover:bg-bg-surface-2 hover:text-text-primary focus-visible:border-accent-cyan focus-visible:bg-bg-surface-2 focus-visible:text-text-primary focus-visible:ring-2 focus-visible:ring-accent-cyan/50"
                >
                  {tile.label}
                </NavLink>
              ),
            )}
          </div>
        ))}
      </div>

      <NavLink
        to={ALL_SETTINGS_HREF}
        onClick={onClose}
        className="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-surface-2 px-4 py-3 text-[13.5px] font-semibold text-accent-cyan outline-none transition-colors hover:text-accent-sky focus-visible:ring-2 focus-visible:ring-accent-cyan/50"
      >
        <span>View all settings</span>
        <ArrowRight className="h-4 w-4" />
      </NavLink>
    </div>
  );
}
